import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { Router } from "@angular/router";
import { BehaviorSubject, Observable } from "rxjs";
import { environment } from "../../environments/environment";
import { ListingModel } from "../_model/property/listing.model";
import { HttpService } from "./http.service";
import { LocalStorage } from "./local-storage.service";
import { UserService } from "./user.service";

@Injectable({
  providedIn: 'root'
})
export class ListingService extends HttpService {

  private url = environment.apiUrl + 'listings';

  //Listings
  private listings$: { [table: string]: BehaviorSubject<ListingModel[]> } = {};

  //Loading
  private loading$: { [table: string]: BehaviorSubject<boolean> } = {};

  //Sorting/Filtering/Searching Params
  private defaultFilter: ListingParams = {
    page: 0,
    size: 50,
    sortBy: null,
    sortOrder: null,
    statuses: null,
    search: null,
    contractStatus: null,
    searchMethod: null
  };
  private filters: { [table: string]: ListingParams } = {};
  private searchMethod: 'elastic' | 'new' = null;

  //timeout for limiting get calls
  private getTimeout = null;

  constructor(
    protected http: HttpClient,
    protected userService: UserService,
    protected router: Router,
    protected dialog: MatDialog,
    protected localStorage: LocalStorage
  ) {
    super(http, userService, router, dialog, localStorage);
  }

  public setSearchMethod(method: 'elastic' | 'new') {
    this.searchMethod = method;
  }

  /**
   * Creates the filters and observables around managing a table's data.
   * Will also re-create errored observables.
   */
  private createTable(table: string, statusKeys?: string[], defaultSort?: string, contractStatus?: string) {
    if (!this.filters[table]) {
      this.filters[table] = { ...this.defaultFilter };
      if (statusKeys?.length) this.filters[table].statuses = statusKeys.toString();
      if (defaultSort) {
        this.filters[table].sortBy = defaultSort;
        this.filters[table].sortOrder = 'DESC';
      }
      if (contractStatus) this.filters[table].contractStatus = contractStatus;
    }
    if (!this.listings$[table]) this.listings$[table] = new BehaviorSubject<ListingModel[]>([]);
    if (this.listings$[table].hasError) this.listings$[table] = new BehaviorSubject<ListingModel[]>([]);
    if (!this.loading$[table]) this.loading$[table] = new BehaviorSubject(false);
  }

  /**
   * Sets the filters for a specific table.
   * Returns the filters.
   */
  public setFilters(table: string, statusKeys?: string[], defaultSort?: string, contractStatus?: string): ListingParams {
    if (this.filters[table]) {
      if (statusKeys.length) this.filters[table].statuses = statusKeys.toString();
      else this.filters[table].statuses = null;
      if (contractStatus) this.filters[table].contractStatus = contractStatus;
    } else {
      this.createTable(table, statusKeys, defaultSort, contractStatus);
    }
    return this.filters[table];
  }

  /**
   * Clears all of the data from the ListingService
   */
  public clear() {
    //listings
    for (let table in this.listings$) this.listings$[table].next([]);
    if (this.getTimeout) clearTimeout(this.getTimeout);
    for (let table in this.loading$) this.loading$[table].next(false);
    //filter
    this.filters = {};
  }

  /**
   * Offsets the filter pagination (so we retrieve the first 2 pages, then the 3rd, 4th, ...).
   * This allows for us an easy way to always have the next page loaded for the user.
   */
  private offsetFilter(table: string): Partial<ListingParams> {
    return {
      page: this.filters[table].page,
      size: this.filters[table].size,
      sortBy: this.filters[table].sortBy,
      sortOrder: this.filters[table].sortOrder,
      statuses: this.filters[table].statuses,
      search: this.filters[table].search?.trim() || null,
      contractStatus: this.filters[table].contractStatus,
      all: table === 'search' ? true : null,
      searchMethod: this.searchMethod
    }
  }

  /**
   * Fetches all the listings for the user, in accordance with the parameters set
   */
  public get(table: string): Observable<ListingModel[]> {
    this.createTable(table);
    this.loading$[table].next(true);
    this.GET(this.url, this.offsetFilter(table)).subscribe(_listings => {
      if (_listings) {
        let __listings = _listings.map(_listing => new ListingModel(_listing));
        //let's just go and preserve the previously shown random icon for no particular reason
        if (this.listings$[table].getValue().length) {
          let previousListings = this.listings$[table].getValue();
          for (let listing of __listings) {
              let previousIcon = previousListings.find(l => l.listingKey === listing.listingKey)?.icon;
              if (previousIcon) listing.icon = previousIcon;
          }
        }
        this.listings$[table].next(__listings);
        this.loading$[table].next(false);
        this.validateModel('ListingModel', _listings[0], new ListingModel(), true, false);
      } else {
        this.loading$[table].next(false);
      }
    }, error => {
      this.listings$[table].error(error);
      this.loading$[table].next(false);
    });
    return this.listings$[table].asObservable();
  }

  /**
   * Indicates whether or not we are still searching for a result
   */
  public loading(table: string): Observable<boolean> {
    this.createTable(table);
    return this.loading$[table].asObservable();
  }

  /**
   * Applies all the filters on the listings, sorts, and emits the result
   */
  private applyFilters(table: string, get: boolean) {
    //we only grab more listings if we need to
    if (this.getTimeout) clearTimeout(this.getTimeout);
    if (get) {
      this.getTimeout = setTimeout(() => this.get(table), 500);
    } else {
      this.loading$[table].next(false);
    }
  }

  /**
   * Sets the status filter, then applies all filters
   */
  public status(table: string, statuses?: string[]) {
    this.createTable(table);
    this.loading$[table].next(true);
    let get: boolean = (!!statuses.length && this.filters[table].statuses !== statuses.toString());
    if (statuses.length) {
      this.filters[table].statuses = statuses.toString();
    } else {
      this.filters[table].statuses = null;
    }
    this.applyFilters(table, get);
  }

  /**
   * Sets the search filter, then applies all filters
   */
  public search(table: string, search?: string) {
    this.createTable(table);
    this.loading$[table].next(true);
    let text = search?.trim() || null;
    let get: boolean = (text && text.length >= 3);
    this.filters[table].page = 0;
    this.filters[table].search = text;
    this.applyFilters(table, get);
  }

  /**
   * Sets the sort column, then applies all filters
   */
  public sort(table: string, sortBy?: string, sortOrder?: 'ASC' | 'DESC' | any) {
    this.createTable(table);
    this.loading$[table].next(true);
    let get: boolean = (!!sortOrder && (this.filters[table].sortBy !== sortBy || this.filters[table].sortOrder !== sortOrder));
    if (sortOrder) {
      this.filters[table].sortBy = sortBy;
      this.filters[table].sortOrder = sortOrder;
    } else {
      this.filters[table].sortBy = null;
      this.filters[table].sortOrder = null;
    }
    this.applyFilters(table, get);
  }

  /**
   * Changes the pagination of the request filter to fetch more data for the user (1 extra page worth)
   */
  public paginate(table: string, page: number, size: number) {
    this.createTable(table);
    this.loading$[table].next(true);
    let get: boolean = (page > this.filters[table].page || this.filters[table].size <= size);
    if (page !== this.filters[table].page || this.filters[table].size !== size) {
      this.filters[table].page = page;
      this.filters[table].size = size;
    }
    this.applyFilters(table, get);
  }

  /**
   * Searches listings, but keeps the state separate from the regular search so it can be used in multiple places.
   * Mostly a copied from search() and modified accordingly, modifications are commented
   */
  public searchListings(search?: string): Observable<ListingModel[]> {
    this.createTable('search');
    this.listings$['search'].next([]); //clear our prev results so we don't show em accidentally
    let text = search && search.trim();
    let get: boolean = (text && text.length >= 3); //removed the search !== last search condition
    /* Clear the filters and hard-set */
    for (let key in this.filters['search']) this.filters['search'][key] = null;
    this.filters['search'].page = 0;
    this.filters['search'].size = 10;
    this.filters['search'].sortBy = 'modificationTimestamp';
    this.filters['search'].sortOrder = 'DESC';
    /* ^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^ */
    this.filters['search'].search = text;
    this.applyFilters('search', get);
    return this.listings$['search'].asObservable(); //return our sub in this case
  }

  /**
   * Removes a listing from the list of listings, does not delete it from the server
   */
  public remove(key: string) {
    //see if there's a non-cached searched versions to stealth remove
    for (let table in this.listings$) {
      let foundIndex = this.listings$[table].getValue().findIndex(_listing => _listing.listingKey === key);
      if (foundIndex !== -1) {
        let listings = this.listings$[table].getValue();
        listings.splice(foundIndex, 1);
        this.listings$[table].next(listings);
      }
    }
  }

  /**
   * Allows a listing in the cache to be updated externally (by the PropertyService).
   * Key is supplied separately because sometimes the key can change to a new one.
   */
  public update(key: string, listing: ListingModel) {
    //see if there's a non-cached searched versions to stealth update
    for (let table of Object.keys(this.filters)) {
      if (this.filters[table].search) {
        let foundNonCached = this.listings$[table].getValue().find(_listing => _listing.listingKey === key || _listing.listingKey === listing.listingKey);
        if (foundNonCached) {
          foundNonCached.overwrite(listing);
          this.listings$[table].next(this.listings$[table].getValue());
        }
      }
    }
  }
}

interface ListingParams {
  page?: number;
  size?: number;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
  statuses?: string;
  search?: string;
  contractStatus?: string;
  all?: boolean;
  searchMethod?: 'elastic' | 'new';
}
